const API_BASE = process.env.REACT_APP_API_URL || "";

// ---------- Auth helpers ----------
function getToken() {
  return localStorage.getItem("token");
}

export { getToken };

function authHeaders(extra = {}) {
  const token = getToken();
  return {
    ...extra,
    ...(token ? { Authorization: `Token ${token}` } : {}),
  };
}

// Source object for react-pdf's <Document file={…}> when the PDF lives behind
// an authenticated endpoint (topic PDFs: /api/topic-pdfs/<id>/file/, reading
// PDFs: /api/reading/pdfs/<id>/file/). The /media/ path no longer serves
// them, so pdf.js must send the token on its own fetches (including range
// requests). Memoise the result at the call site — react-pdf reloads the
// document whenever the `file` object identity changes.
export function authenticatedPdfSource(url) {
  const token = getToken();
  return { url, httpHeaders: token ? { Authorization: `Token ${token}` } : {} };
}

// Append the exact reset time to a rate-limit message (retry_at is an ISO
// timestamp from the server; shown in the user's local timezone).
export function withRetryTime(detail, retryAt) {
  if (!retryAt) return detail;
  const d = new Date(retryAt);
  if (isNaN(d.getTime())) return detail;
  const when = d.toLocaleString(undefined, {
    weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
  return `${detail} You can try again on ${when}.`;
}

// Build an Error from a failed response, adding the precise retry time on 429.
async function apiError(res, fallback) {
  const data = await res.json().catch(() => ({}));
  const detail = data.detail || data.error || fallback;
  const e = new Error(res.status === 429 ? withRetryTime(detail, data.retry_at) : detail);
  e.status = res.status;
  e.retryAt = data.retry_at || null;
  e.code = data.code || null;
  e.signupCta = !!data.signup_cta;
  return e;
}

export function setToken(token) {
  localStorage.setItem("token", token);
}

export function clearToken() {
  localStorage.removeItem("token");
}

// POST /api/auth/token/  { username, password } -> { token }
export async function login(username, password) {
  const res = await fetch(`${API_BASE}/api/auth/token/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });

  if (!res.ok) {
    throw new Error("Login failed (check username/password)");
  }

  const data = await res.json(); // { token: "..." }
  if (!data.token) throw new Error("No token returned from server");
  setToken(data.token);
  return data;
}

// POST /api/auth/signup/  { email } -> { detail }
export async function signup(email) {
  const res = await fetch(`${API_BASE}/api/auth/signup/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Signup failed");
  }

  return res.json();
}

// POST /api/auth/verify-code/  { email, code } -> { valid: true }
export async function verifyCode(email, code) {
  const res = await fetch(`${API_BASE}/api/auth/verify-code/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, code }),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Invalid or expired code.");
  }

  return res.json();
}

// POST /api/auth/verify/
// Link path: verifyAccount({ token, password })
// Code path: verifyAccount({ email, code, password })
export async function verifyAccount(payload) {
  const res = await fetch(`${API_BASE}/api/auth/verify/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Verification failed.");
  }

  return res.json();
}

export function logout() {
  localStorage.removeItem("token");
}

// POST /api/auth/password-reset/  { email } -> { detail }
export async function requestPasswordReset(email) {
  const res = await fetch(`${API_BASE}/api/auth/password-reset/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not send the reset email.");
  }
  return res.json();
}

// POST /api/auth/password-reset/confirm/  { email, code, password } -> { token }
export async function confirmPasswordReset({ email, code, password }) {
  const res = await fetch(`${API_BASE}/api/auth/password-reset/confirm/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, code, password }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not reset your password.");
  }
  const data = await res.json();
  if (data.token) setToken(data.token);
  return data;
}

// GET /api/billing/individual-pricing/
// -> { monthly_price_cents, yearly_monthly_price_cents, yearly_charge_cents, currency }
export async function getIndividualPricing() {
  const res = await fetch(`${API_BASE}/api/billing/individual-pricing/`);
  if (!res.ok) throw new Error("Could not load pricing.");
  return res.json();
}

// POST /api/billing/start-trial/ -> { trial_end }
// Grants the one-time free trial to a logged-in user who never got one (an
// abandoned signup rescued at login). Signup itself grants the trial inline via
// verifyAccount — no Stripe, and no card, until they choose to subscribe.
export async function startTrial() {
  const res = await fetch(`${API_BASE}/api/billing/start-trial/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not start your free trial.");
  }
  return res.json();
}

// POST /api/billing/checkout-complete/  { session_id } -> { token }
export async function completeCheckout(sessionId) {
  const res = await fetch(`${API_BASE}/api/billing/checkout-complete/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: sessionId }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not verify your payment.");
  }
  return res.json();
}

// POST /api/billing/resubscribe/ -> { url }  (authenticated; new Checkout, no trial)
// plan: "monthly" (default) or "yearly" (discounted, billed yearly up front)
export async function resubscribe(plan = "monthly") {
  const res = await fetch(`${API_BASE}/api/billing/resubscribe/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ plan }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not start checkout.");
  }
  return res.json();
}

// POST /api/billing/portal/ -> { url }  (authenticated; manage/update billing)
export async function openBillingPortal() {
  const res = await fetch(`${API_BASE}/api/billing/portal/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not open the billing portal.");
  }
  return res.json();
}

// ---------- Institute accounts ----------

// GET /api/billing/institute-pricing/ -> { min_seats, seat_yearly_price_cents, currency }
export async function getInstitutePricing() {
  const res = await fetch(`${API_BASE}/api/billing/institute-pricing/`);
  if (!res.ok) throw new Error("Could not load institute pricing.");
  return res.json();
}

// POST /api/billing/institute-checkout/
// payload: { email, code, password, institute_name, seats, first_name, last_name, gender, date_of_birth } -> { url }
export async function createInstituteCheckout(payload) {
  const res = await fetch(`${API_BASE}/api/billing/institute-checkout/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not start checkout.");
  }
  return res.json();
}

// POST /api/billing/institute-checkout-complete/  { session_id } -> { token }
export async function completeInstituteCheckout(sessionId) {
  const res = await fetch(`${API_BASE}/api/billing/institute-checkout-complete/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: sessionId }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not verify your payment.");
  }
  return res.json();
}

// POST /api/institute/billing-portal/ -> { url }  (institute admin)
export async function openInstituteBillingPortal() {
  const res = await fetch(`${API_BASE}/api/institute/billing-portal/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not open the billing portal.");
  }
  return res.json();
}

// GET /api/institute/members/ -> { members, seats_purchased, seats_used }  (institute admin)
export async function instituteMembers() {
  const res = await fetch(`${API_BASE}/api/institute/members/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Could not load your members.");
  return res.json();
}

// POST /api/institute/members/  { email, first_name, last_name, gender, date_of_birth?, role, temp_password } -> { member }
export async function instituteAddMember(payload) {
  const res = await fetch(`${API_BASE}/api/institute/members/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not create the account.");
  }
  return res.json();
}

// PATCH /api/institute/members/<id>/  { role? , reset_password? } -> { member }
export async function instituteUpdateMember(id, payload) {
  const res = await fetch(`${API_BASE}/api/institute/members/${id}/`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not update the member.");
  }
  return res.json();
}

// DELETE /api/institute/members/<id>/
export async function instituteDeleteMember(id) {
  const res = await fetch(`${API_BASE}/api/institute/members/${id}/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not delete the member.");
  }
}

// POST /api/institute/admin-seat/  { take_seat } -> { admin_takes_seat, seats_purchased, seats_used }
export async function instituteSetAdminSeat(takeSeat) {
  const res = await fetch(`${API_BASE}/api/institute/admin-seat/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ take_seat: takeSeat }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not update your seat.");
  }
  return res.json();
}

// POST /api/institute/seats/  { seats } -> { seats_purchased, seats_used }
export async function instituteIncreaseSeats(seats) {
  const res = await fetch(`${API_BASE}/api/institute/seats/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ seats }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not update your seats.");
  }
  return res.json();
}

// POST /api/institute/topics/<id>/share/  { shared } -> { id, is_institute_shared }
export async function instituteShareTopic(id, shared) {
  const res = await fetch(`${API_BASE}/api/institute/topics/${id}/share/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ shared }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not update topic sharing.");
  }
  return res.json();
}

// PATCH /api/institute/settings/  { name?, hide_global_topics?, hide_group_shared_topics? }
//   -> { name, hide_global_topics, hide_group_shared_topics }
export async function instituteUpdateSettings(payload) {
  const res = await fetch(`${API_BASE}/api/institute/settings/`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not save your settings.");
  }
  return res.json();
}

// GET /api/institute/api-key/ -> { has_key, prefix, created_at }  (institute admin)
export async function instituteApiKey() {
  const res = await fetch(`${API_BASE}/api/institute/api-key/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Could not load your API key status.");
  return res.json();
}

// POST /api/institute/api-key/ -> { api_key, prefix, created_at }  (raw key shown once)
export async function instituteGenerateApiKey() {
  const res = await fetch(`${API_BASE}/api/institute/api-key/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not generate an API key.");
  }
  return res.json();
}

// DELETE /api/institute/api-key/
export async function instituteRevokeApiKey() {
  const res = await fetch(`${API_BASE}/api/institute/api-key/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not revoke the API key.");
  }
}

// GET /api/admin/institutes/ -> { institutes: [...] }  (site admin)
export async function adminListInstitutes() {
  const res = await fetch(`${API_BASE}/api/admin/institutes/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Could not load institutes.");
  return res.json();
}

// POST /api/admin/institutes/<id>/approve/ -> { institute }
export async function adminApproveInstitute(id) {
  const res = await fetch(`${API_BASE}/api/admin/institutes/${id}/approve/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not approve the institute.");
  }
  return res.json();
}

// POST /api/admin/institutes/<id>/disable/ -> { institute }
export async function adminDisableInstitute(id) {
  const res = await fetch(`${API_BASE}/api/admin/institutes/${id}/disable/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not disable the institute.");
  }
  return res.json();
}

// DELETE /api/admin/institutes/<id>/  { confirm_name } — permanent, deletes members too
export async function adminDeleteInstitute(id, confirmName) {
  const res = await fetch(`${API_BASE}/api/admin/institutes/${id}/`, {
    method: "DELETE",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ confirm_name: confirmName }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not delete the institute.");
  }
}

// POST /api/institute/leave/ — leave a disabled/lapsed institute
export async function instituteLeave() {
  const res = await fetch(`${API_BASE}/api/institute/leave/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not leave the institute.");
  }
  return res.json();
}

// POST /api/billing/redeem-voucher/  { code } -> { voucher_access_until }
export async function redeemVoucher(code) {
  const res = await fetch(`${API_BASE}/api/billing/redeem-voucher/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ code }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not redeem that voucher.");
  }
  return res.json();
}

// ---------- Admin: access vouchers ----------
export async function adminListVouchers() {
  const res = await fetch(`${API_BASE}/api/admin/vouchers/`, { headers: authHeaders() });
  if (!res.ok) throw new Error("Failed to load vouchers");
  return res.json();
}

export async function adminCreateVoucher(payload) {
  const res = await fetch(`${API_BASE}/api/admin/vouchers/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not create the voucher.");
  }
  return res.json();
}

export async function adminRevokeVoucher(id) {
  const res = await fetch(`${API_BASE}/api/admin/vouchers/${id}/revoke/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not revoke the voucher.");
  }
  return res.json();
}

// ---------- Existing APIs (now auth-aware) ----------
export async function fetchTopics() {
  const res = await fetch(`${API_BASE}/api/topics/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to load topics");
  return res.json();
}

export async function reorderTopics(ids) {
  const res = await fetch(`${API_BASE}/api/topics/reorder/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) throw new Error("Failed to save order");
  return res.json();
}

// ---------- Topic categories (sidebar groupings) ----------
// GET /api/topic-categories/ -> [{ id, name, description, order }] in sidebar order.
export async function fetchTopicCategories() {
  const res = await fetch(`${API_BASE}/api/topic-categories/`, { headers: authHeaders() });
  if (!res.ok) throw new Error("Failed to load categories");
  return res.json();
}

async function categoryError(res, fallback) {
  const data = await res.json().catch(() => ({}));
  const msg = data.detail || (Array.isArray(data.name) && data.name[0]) || (Array.isArray(data.description) && data.description[0]);
  return new Error(msg || fallback);
}

// Admin: POST /api/topic-categories/ { name, description, include_in_review, icon, is_restricted } -> the new category.
// include_in_review (default true): whether Start Review may pick this category's topics.
// icon: a key from categoryIcons.js ("" = folder); the API rejects anything else.
// is_restricted (default false): only admins and members of a category group that
// contains this category can see it and its topics.
export async function createTopicCategory({ name, description = "", include_in_review = true, icon = "", is_restricted = false }) {
  const res = await fetch(`${API_BASE}/api/topic-categories/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ name, description, include_in_review, icon, is_restricted }),
  });
  if (!res.ok) throw await categoryError(res, "Could not create the category.");
  return res.json();
}

// Admin: PATCH /api/topic-categories/<id>/ { name?, description?, include_in_review?, icon?, is_restricted? } -> updated category.
export async function updateTopicCategory(id, fields) {
  const res = await fetch(`${API_BASE}/api/topic-categories/${id}/`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(fields),
  });
  if (!res.ok) throw await categoryError(res, "Could not update the category.");
  return res.json();
}

// Admin: DELETE /api/topic-categories/<id>/ — its topics drop back to the top level.
export async function deleteTopicCategory(id) {
  const res = await fetch(`${API_BASE}/api/topic-categories/${id}/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) throw await categoryError(res, "Could not delete the category.");
}

// Admin: POST /api/topic-categories/reorder/ { ids } — same contract as reorderTopics.
export async function reorderTopicCategories(ids) {
  const res = await fetch(`${API_BASE}/api/topic-categories/reorder/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) throw new Error("Failed to save category order");
  return res.json();
}

// ── Category groups (admin) — access groups for restricted categories ──

async function groupError(res, fallback) {
  const data = await res.json().catch(() => ({}));
  const msg =
    data.detail ||
    (Array.isArray(data.name) && data.name[0]) ||
    (Array.isArray(data.category_ids) && data.category_ids[0]) ||
    (Array.isArray(data.institute_ids) && data.institute_ids[0]) ||
    (Array.isArray(data.email) && data.email[0]);
  return new Error(msg || fallback);
}

// GET /api/admin/category-groups/ -> { groups: [{ id, name, description, category_ids, institute_ids,
//   institutes: [{id, name, status, operational, seats_used, seats_purchased}],
//   members: [{id, email, display_name}], member_count }] }
export async function adminListCategoryGroups() {
  const res = await fetch(`${API_BASE}/api/admin/category-groups/`, { headers: authHeaders() });
  if (!res.ok) throw await groupError(res, "Could not load category groups.");
  return res.json();
}

// POST /api/admin/category-groups/ { name, description?, category_ids? } -> group
export async function adminCreateCategoryGroup({ name, description = "", category_ids = [] }) {
  const res = await fetch(`${API_BASE}/api/admin/category-groups/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ name, description, category_ids }),
  });
  if (!res.ok) throw await groupError(res, "Could not create the group.");
  return res.json();
}

// PATCH /api/admin/category-groups/<id>/ { name?, description?, category_ids?, institute_ids? } -> group
// category_ids / institute_ids replace the whole set.
export async function adminUpdateCategoryGroup(id, fields) {
  const res = await fetch(`${API_BASE}/api/admin/category-groups/${id}/`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(fields),
  });
  if (!res.ok) throw await groupError(res, "Could not update the group.");
  return res.json();
}

// DELETE /api/admin/category-groups/<id>/ — revokes members' access; categories/topics untouched.
export async function adminDeleteCategoryGroup(id) {
  const res = await fetch(`${API_BASE}/api/admin/category-groups/${id}/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) throw await groupError(res, "Could not delete the group.");
}

// POST /api/admin/category-groups/<id>/members/ { email } -> group (404 if no such account)
export async function adminAddCategoryGroupMember(id, email) {
  const res = await fetch(`${API_BASE}/api/admin/category-groups/${id}/members/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw await groupError(res, "Could not add the member.");
  return res.json();
}

// DELETE /api/admin/category-groups/<id>/members/<userId>/ -> group
export async function adminRemoveCategoryGroupMember(id, userId) {
  const res = await fetch(`${API_BASE}/api/admin/category-groups/${id}/members/${userId}/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) throw await groupError(res, "Could not remove the member.");
  return res.json();
}

// Admin: POST /api/topics/<id>/category/ { category_id | null } -> { id, category }.
export async function setTopicCategory(topicId, categoryId) {
  const res = await fetch(`${API_BASE}/api/topics/${topicId}/category/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ category_id: categoryId ?? null }),
  });
  if (!res.ok) throw await categoryError(res, "Could not move the topic.");
  return res.json();
}

export async function deleteTopic(id) {
  const res = await fetch(`${API_BASE}/api/topics/${id}/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to delete topic");
}

export async function fetchTopicDetail(id) {
  const res = await fetch(`${API_BASE}/api/topics/${id}/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to load topic");
  return res.json();
}

export async function fetchWordDetail(id) {
  const res = await fetch(`${API_BASE}/api/words/${id}/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to load word");
  return res.json();
}

// Admin: existing base words a prefixed form (و/ال) could link its definition to.
export async function fetchLemmaCandidates(wordId) {
  const res = await fetch(`${API_BASE}/api/words/${wordId}/lemma-link/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to load link suggestions");
  return res.json(); // { candidates: [{ id, text, definition }] }
}

// Admin: set (or clear, with lemmaId = null) a word's lemma so it shares a definition.
export async function linkWordLemma(wordId, lemmaId) {
  const res = await fetch(`${API_BASE}/api/words/${wordId}/lemma-link/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ lemma_id: lemmaId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to link definition");
  }
  return res.json();
}

export async function fetchWordTopics(id) {
  const res = await fetch(`${API_BASE}/api/words/${id}/topics/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to load topics for word");
  return res.json();
}

// Global search: words, roots, sentences and topics in one grouped response.
// opts: { signal?: AbortSignal, scope?: "all"|"words"|"sentences"|"topics"|"roots", limit?: number }
export async function globalSearch(query, opts = {}) {
  const params = new URLSearchParams({ q: query });
  if (opts.scope) params.set("scope", opts.scope);
  if (opts.limit) params.set("limit", String(opts.limit));
  const res = await fetch(`${API_BASE}/api/search/?${params.toString()}`, {
    headers: authHeaders(),
    signal: opts.signal,
  });
  if (!res.ok) throw new Error("Search failed");
  return res.json();
}

export async function searchWords(query) {
  const res = await fetch(
    `${API_BASE}/api/words/?search=${encodeURIComponent(query)}`,
    { headers: authHeaders() }
  );
  if (!res.ok) throw new Error("Failed to search words");
  return res.json();
}

// ---------- Reading (per-user PDFs) ----------
export async function fetchReadingPDFs() {
  const res = await fetch(`${API_BASE}/api/reading/pdfs/`, { headers: authHeaders() });
  if (!res.ok) throw new Error("Failed to load reading PDFs");
  return res.json();
}

export async function uploadReadingPDF(file, title = "") {
  const formData = new FormData();
  formData.append("file", file);
  if (title) formData.append("title", title);
  const res = await fetch(`${API_BASE}/api/reading/pdfs/`, {
    method: "POST",
    headers: { Authorization: `Token ${getToken()}` }, // browser sets multipart boundary
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to upload PDF");
  }
  return res.json();
}

export async function deleteReadingPDF(id) {
  const res = await fetch(`${API_BASE}/api/reading/pdfs/${id}/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok && res.status !== 204) throw new Error("Failed to delete PDF");
}

// Save the resume position (best-effort)
export async function updateReadingPDFPage(id, lastPage) {
  try {
    await fetch(`${API_BASE}/api/reading/pdfs/${id}/`, {
      method: "PATCH",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ last_page: lastPage }),
    });
  } catch { /* ignore */ }
}

// Create a personal "book topic" from selected PDF text.
export async function createBookTopic({ pdfId = null, pageNumber = null, text, title = "", enrichAi = false } = {}) {
  const res = await fetch(`${API_BASE}/api/reading/create-topic/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ pdf_id: pdfId, page_number: pageNumber, text, title, enrich_ai: enrichAi }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to create topic");
  }
  return res.json();
}

// OCR / cache-probe a scanned page. Passing pdfId+pageNumber (own reading PDF)
// or topicPdfId+pageNumber (shared topic PDF, global cache) enables caching:
// a cached page returns instantly (no Vision call); omitting imageBase64 is a
// pure cache probe (page open). force (staff only) re-detects over a bad
// cached topic page. -> { width, height, words, cached }
export async function ocrReadingPage({ imageBase64 = "", pdfId = null, topicPdfId = null, pageNumber = null, force = false } = {}) {
  const body = {};
  if (imageBase64) body.image_base64 = imageBase64;
  if (pdfId != null) body.pdf_id = pdfId;
  if (topicPdfId != null) body.topic_pdf_id = topicPdfId;
  if (pageNumber != null) body.page_number = pageNumber;
  if (force) body.force = true;
  const res = await fetch(`${API_BASE}/api/reading/ocr/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await apiError(res, "OCR failed");
  return res.json();
}

// Translate a selected sentence (Reading translate mode). Cache-first on the
// server: a sentence anyone already translated returns { cached: true } free.
// -> { translation, words: [{word, definition, transliteration, pos}], cached, remaining }
export async function translateReadingSentence(text) {
  const res = await fetch(`${API_BASE}/api/reading/translate-sentence/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw await apiError(res, "Translation failed");
  return res.json();
}

// Persist OCR word-text corrections into the cached page (owner only), so
// future sessions and the iʿrāb/translation caches see the fixed words.
// corrections: [{index, text}] — index into the page's OCR words array.
export async function correctReadingOcrWords({ pdfId, pageNumber, corrections }) {
  const res = await fetch(`${API_BASE}/api/reading/ocr/words/`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ pdf_id: pdfId, page_number: pageNumber, corrections }),
  });
  if (!res.ok) throw await apiError(res, "Could not save corrections");
  return res.json();
}

// ---------- Word Bank APIs ----------
// GET /api/word-bank/ -> { entries: [{ id, word, pos, status, added_at, review_item_id, is_known }] }
export async function fetchWordBank() {
  const res = await fetch(`${API_BASE}/api/word-bank/`, { headers: authHeaders() });
  if (!res.ok) throw new Error("Failed to load Word Bank");
  return res.json();
}

// POST /api/word-bank/ -> { entry, already_in_bank }
// payload: { word_id } OR { text, pos, transliteration?, definition? }
export async function addWordBankEntry(payload) {
  const res = await fetch(`${API_BASE}/api/word-bank/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await apiError(res, "Failed to add word");
  return res.json();
}

// POST /api/word-bank/<entryId>/request-definition/ -> { ok, status, definition_requested }
// Ask an admin to supply a definition for a word whose AI look-up was unavailable.
export async function requestWordBankDefinition(entryId) {
  const res = await fetch(`${API_BASE}/api/word-bank/${entryId}/request-definition/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
  });
  if (!res.ok) throw await apiError(res, "Failed to request a definition");
  return res.json();
}

export async function deleteWordBankEntry(entryId) {
  const res = await fetch(`${API_BASE}/api/word-bank/${entryId}/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok && res.status !== 204) throw new Error("Failed to remove word");
}

// Reset a learnt word so it can be quizzed again
export async function relearnWordBankWord(wordId) {
  const res = await fetch(`${API_BASE}/api/word-bank/relearn/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ word_id: wordId }),
  });
  if (!res.ok) throw new Error("Failed to reset word");
  return res.json();
}

// Ask the AI how to say something in Arabic — returns up to 3 variations (not saved)
export async function askSentenceAI(text) {
  const res = await fetch(`${API_BASE}/api/word-bank/sentences/ask/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw await apiError(res, "Failed to get suggestions");
  return res.json();
}

// Live-conversation Speaking Helper — type English, get up to 3 fuṣḥā replies
// (Arabic with harakat + translation). Nothing is saved. { replies, asks_remaining }
export async function askSpeakingHelper(text) {
  const res = await fetch(`${API_BASE}/api/speaking-helper/ask/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw await apiError(res, "Failed to get suggestions");
  return res.json();
}

// EXPERIMENTAL — given an Arabic transcript of what the call partner just said,
// returns up to 3 fuṣḥā replies the learner could say back. { replies, remaining }
// Gated server-side by SiteConfig.auto_suggest_enabled + daily limit.
export async function autoSuggestReplies(text) {
  const res = await fetch(`${API_BASE}/api/speaking-helper/auto-suggest/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw await apiError(res, "Failed to get suggestions");
  return res.json();
}

// Clean up a raw STT transcript line: fix likely errors, add harakat + translation.
// Returns { arabic, translation, remaining }. Shares the auto-suggest daily budget.
export async function correctTranscript(text) {
  const res = await fetch(`${API_BASE}/api/speaking-helper/correct/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw await apiError(res, "Failed to correct transcript");
  return res.json();
}

export async function fetchWordBankSentences() {
  const res = await fetch(`${API_BASE}/api/word-bank/sentences/`, { headers: authHeaders() });
  if (!res.ok) throw new Error("Failed to load sentences");
  return res.json();
}

export async function saveWordBankSentence({ arabic, translation }) {
  const res = await fetch(`${API_BASE}/api/word-bank/sentences/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ arabic, translation }),
  });
  if (!res.ok) throw new Error("Failed to save sentence");
  return res.json();
}

export async function deleteWordBankSentence(id) {
  const res = await fetch(`${API_BASE}/api/word-bank/sentences/${id}/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok && res.status !== 204) throw new Error("Failed to delete sentence");
}

// Lookup for a single Arabic word (definition/transliteration/pos) — not saved.
// Cache-first on the server (fed by past lookups and sentence translations):
// a hit returns { cached: true } free. Pass { probe: true } to only consult
// the cache — a miss returns { cached: false } and never calls the AI.
export async function analyzeWordBankWord(text, { probe = false } = {}) {
  const body = { text };
  if (probe) body.probe = true;
  const res = await fetch(`${API_BASE}/api/word-bank/analyze-word/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await apiError(res, "Failed to analyze the word");
  return res.json();
}

// Mark a saved sentence as just used (bumps it to the top of the list)
export async function touchWordBankSentence(id) {
  const res = await fetch(`${API_BASE}/api/word-bank/sentences/${id}/played/`, {
    method: "POST",
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to update sentence");
  return res.json();
}

// Verb-form drill questions for the caller's Word Bank verbs
export async function fetchWordBankVerbFormQuestions() {
  const res = await fetch(`${API_BASE}/api/learning/word-bank/verb-form-questions/`, {
    headers: authHeaders(),
  });
  if (!res.ok) return { questions: [] };
  return res.json();
}

// "Which particle goes with this verb?" questions for the caller's Word Bank
export async function fetchWordBankParticleQuestions() {
  const res = await fetch(`${API_BASE}/api/learning/word-bank/particle-questions/`, {
    headers: authHeaders(),
  });
  if (!res.ok) return { questions: [] };
  return res.json();
}

// Admin: words for review — status "pending" (default) or "rejected"
export async function fetchPendingWordBankWords(status = "pending") {
  const res = await fetch(
    `${API_BASE}/api/admin/word-bank/pending/?status=${encodeURIComponent(status)}`,
    { headers: authHeaders() }
  );
  if (!res.ok) throw new Error("Failed to load words");
  return res.json();
}

// Admin: approve | reject a word
export async function moderateWordBankWord(wordId, action) {
  const res = await fetch(`${API_BASE}/api/admin/word-bank/${wordId}/moderate/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ action }),
  });
  if (!res.ok) throw new Error("Failed to moderate word");
  return res.json();
}

// Admin: AI-classify one or more words' part of speech (sets Word.pos)
export async function classifyWords(wordIds) {
  const res = await fetch(`${API_BASE}/api/admin/word-bank/classify/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ word_ids: wordIds }),
  });
  if (!res.ok) throw await apiError(res, "Failed to classify words");
  return res.json();
}

// Admin: generate + save example sentences for a word via AI
export async function fetchExamplesFromAI(wordId, { count = 3, model = "", preview = false } = {}) {
  const res = await fetch(`${API_BASE}/api/words/${wordId}/fetch-examples-ai/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ count, model, preview }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || err.error || "Failed to fetch examples from AI");
  }
  return res.json();
}

// Admin: save the public definition and/or example sentences for a word
// (manual edit, or after verifying AI-previewed content).
export async function saveWordContent(wordId, { definition, examples } = {}) {
  const body = {};
  if (definition !== undefined) body.definition = definition;
  if (examples !== undefined) body.examples = examples;
  const res = await fetch(`${API_BASE}/api/words/${wordId}/content/`, {
    method: "PUT",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to save content");
  }
  return res.json();
}

// Admin: permanently delete a user-submitted, non-public word
export async function deleteWordBankWord(wordId) {
  const res = await fetch(`${API_BASE}/api/admin/word-bank/${wordId}/moderate/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok && res.status !== 204) throw new Error("Failed to delete word");
}

// ---------- Learning APIs ----------
export async function fetchTopicQuizItems(topicId, mode = "learn", limit = 30, ids = null) {
  let url = `${API_BASE}/api/learning/topics/${topicId}/quiz-items/?mode=${encodeURIComponent(mode)}&limit=${encodeURIComponent(limit)}`;
  if (ids && ids.length > 0) {
    url += `&ids=${ids.join(",")}`;
  }
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) throw new Error("Failed to load quiz items");
  return res.json();
}

// GET /api/learning/topics/<topicId>/word-review-items/ -> { map: { "<word_id>": review_item_id } }
export async function fetchTopicWordReviewItems(topicId) {
  const res = await fetch(
    `${API_BASE}/api/learning/topics/${topicId}/word-review-items/`,
    { headers: authHeaders() }
  );
  if (!res.ok) throw new Error("Failed to load word review items");
  return res.json();
}

// GET /api/learning/items/<reviewItemId>/question/?type=... -> { review_item_id, question }
export async function fetchQuizQuestion(reviewItemId, type = "word_meaning") {
  const res = await fetch(
    `${API_BASE}/api/learning/items/${reviewItemId}/question/?type=${encodeURIComponent(type)}`,
    { headers: authHeaders() }
  );
  if (!res.ok) throw new Error("Failed to generate question");
  return res.json();
}

export async function fetchReviewQueue(limit = 30) {
  const url = `${API_BASE}/api/learning/review/queue/?limit=${encodeURIComponent(
    limit
  )}`;
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) throw new Error("Failed to load review queue");
  return res.json();
}

export async function submitReviewAnswer({
  review_item_id,
  question_type,
  is_correct,
  user_answer = {},
  correct_answer = {},
  // When false, the answer is recorded but the word's learned (`is_known`)
  // state is left untouched — used by the guided Quiz step so testing a word
  // never changes whether it counts as learned.
  update_known = true,
  // When true (and correct), permanently marks the word learned in the guided
  // Vocabulary step. Only the Vocabulary step sends this.
  mark_vocab_learned = false,
}) {
  const res = await fetch(`${API_BASE}/api/learning/review/submit/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({
      review_item_id,
      question_type,
      is_correct,
      user_answer,
      correct_answer,
      update_known,
      mark_vocab_learned,
    }),
  });

  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Submit failed: ${txt}`);
  }

  return res.json();
}

// POST /api/topics/import/  (multipart file upload) -> { topic_id, topic_name, vocab_count, scenario_turns }
// Publish or unpublish an admin-owned topic. is_public=true makes it visible
// to all learners; false hides it (private to the admin). Admin only.
export async function setTopicVisibility(id, isPublic) {
  const res = await fetch(`${API_BASE}/api/topics/${id}/visibility/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ is_public: isPublic }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || data.error || "Failed to update visibility");
  }
  return res.json();
}

// Get-or-create the topic's anonymous share link token (/t/<token>). Admin
// only; idempotent — repeat calls return the same token. 400 when the topic
// isn't publicly shareable (unpublished, institute, book/grammar lesson).
export async function createTopicShareLink(id) {
  const res = await fetch(`${API_BASE}/api/topics/${id}/share/`, {
    method: "POST",
    headers: authHeaders(),
  });
  if (!res.ok) throw await apiError(res, "Failed to create the share link");
  return res.json();
}

// Generate a brand-new (private) topic from a free-text prompt via OpenAI. Admin only.
export async function generateTopicFromPrompt(prompt) {
  const res = await fetch(`${API_BASE}/api/admin/topics/ai-generate/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ prompt }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || data.detail || `Failed to generate topic (HTTP ${res.status})`);
  }
  return res.json();
}

export async function importTopicFromFile(file) {
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${API_BASE}/api/topics/import/`, {
    method: "POST",
    headers: authHeaders(), // no Content-Type — browser sets multipart boundary automatically
    body: formData,
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || "Import failed");
  }

  return res.json();
}

// ---------- Admin edit APIs ----------

// GET /api/auth/me/ -> { id, email, is_staff }
export async function fetchCurrentUser() {
  const res = await fetch(`${API_BASE}/api/auth/me/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to fetch current user");
  return res.json();
}

// PATCH /api/words/<id>/edit/ -> updated Word object
export async function patchWord(id, data) {
  const res = await fetch(`${API_BASE}/api/words/${id}/edit/`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    // Surface DRF field errors (e.g. {"text": ["...already exists."]}), not just `detail`.
    const fieldMsg = Array.isArray(errData.text) ? errData.text[0] : errData.text;
    const msg = errData.detail || fieldMsg ||
      (typeof errData === "object" ? Object.values(errData).flat().join(" ") : "") ||
      "Failed to update word";
    const e = new Error(msg);
    e.data = errData;
    throw e;
  }
  return res.json();
}

// PATCH /api/senses/<id>/ -> updated Sense object
export async function patchSense(id, data) {
  const res = await fetch(`${API_BASE}/api/senses/${id}/`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to update sense");
  }
  return res.json();
}

// DELETE /api/senses/<id>/
export async function deleteSense(id) {
  const res = await fetch(`${API_BASE}/api/senses/${id}/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to delete sense");
  }
}

// POST /api/senses/<id>/set-primary/
export async function setPrimarySense(id) {
  const res = await fetch(`${API_BASE}/api/senses/${id}/set-primary/`, {
    method: "POST",
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to set primary sense");
  }
  return res.json();
}

// POST /api/senses/<sense_id>/extra-forms/ -> new SenseExtraForm
export async function addSenseExtraForm(senseId, payload) {
  const res = await fetch(`${API_BASE}/api/senses/${senseId}/extra-forms/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to add extra form");
  }
  return res.json();
}

// PATCH /api/sense-extra-forms/<id>/
export async function patchSenseExtraForm(id, payload) {
  const res = await fetch(`${API_BASE}/api/sense-extra-forms/${id}/`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to update extra form");
  }
  return res.json();
}

// DELETE /api/sense-extra-forms/<id>/
export async function deleteSenseExtraForm(id) {
  const res = await fetch(`${API_BASE}/api/sense-extra-forms/${id}/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to delete extra form");
  }
}

// Merge a duplicate word into an existing one (admin). Returns the target word.
export async function mergeWord(sourceId, { target_id, add_particle = "" } = {}) {
  const res = await fetch(`${API_BASE}/api/words/${sourceId}/merge-into/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ target_id, add_particle }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to merge word");
  }
  return res.json();
}

// ── Verb governing particles (ḥarf) ──
export async function addSenseParticle(senseId, payload) {
  const res = await fetch(`${API_BASE}/api/senses/${senseId}/particles/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to add particle");
  }
  return res.json();
}

export async function patchSenseParticle(id, payload) {
  const res = await fetch(`${API_BASE}/api/sense-particles/${id}/`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to update particle");
  }
  return res.json();
}

export async function deleteSenseParticle(id) {
  const res = await fetch(`${API_BASE}/api/sense-particles/${id}/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok && res.status !== 204) throw new Error("Failed to delete particle");
}

// POST /api/words/<wordId>/senses/ -> new Sense object
export async function createSense(wordId, definition) {
  const res = await fetch(`${API_BASE}/api/words/${wordId}/senses/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ definition }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to create sense");
  }
  return res.json();
}

// POST /api/words/<wordId>/plurals/ -> new Plural object
export async function addPlural(wordId, data) {
  const res = await fetch(`${API_BASE}/api/words/${wordId}/plurals/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to add plural");
  }
  return res.json();
}

// PATCH /api/plurals/<id>/ -> updated Plural object
export async function patchPlural(id, data) {
  const res = await fetch(`${API_BASE}/api/plurals/${id}/`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to update plural");
  }
  return res.json();
}

// DELETE /api/plurals/<id>/
export async function deletePlural(id) {
  const res = await fetch(`${API_BASE}/api/plurals/${id}/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to delete plural");
  }
}

export async function linkSingular(pluralWordId, singularWordId) {
  const res = await fetch(`${API_BASE}/api/words/${pluralWordId}/link-singular/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ singular_word_id: singularWordId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to link singular");
  }
  return res.json();
}

// PATCH /api/scenario-turns/<id>/ -> updated ScenarioTurn object
export async function patchScenarioTurn(id, data) {
  const res = await fetch(`${API_BASE}/api/scenario-turns/${id}/`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to update scenario turn");
  }
  return res.json();
}

// DELETE /api/scenario-turns/<id>/
export async function deleteScenarioTurn(id) {
  const res = await fetch(`${API_BASE}/api/scenario-turns/${id}/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to delete scenario turn");
  }
}

// POST /api/topics/<topicId>/scenario-turns/ -> new ScenarioTurn
export async function createScenarioTurn(topicId, data) {
  const res = await fetch(`${API_BASE}/api/topics/${topicId}/scenario-turns/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to create scenario turn");
  }
  return res.json();
}

export async function reorderScenarioTurns(topicId, orderedIds) {
  const res = await fetch(`${API_BASE}/api/topics/${topicId}/scenario-turns/reorder/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ ordered_ids: orderedIds }),
  });
  if (!res.ok) throw new Error("Failed to reorder dialogue");
  return res.json();
}

// POST /api/topics/<topicId>/quiz-words/ -> { word, sense_id }
export async function createQuizWord(topicId, data) {
  const res = await fetch(`${API_BASE}/api/topics/${topicId}/quiz-words/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to create quiz word");
  }
  return res.json();
}

// POST /api/topics/<topicId>/add-word/ -> { word, word_type, already_linked }
export async function addTopicWord(topicId, data) {
  const res = await fetch(`${API_BASE}/api/topics/${topicId}/add-word/`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.detail || "Failed to add word to topic");
  return json;
}

export async function deleteTopicWord(topicId, wordId) {
  const res = await fetch(`${API_BASE}/api/topics/${topicId}/words/${wordId}/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok && res.status !== 404) {
    throw new Error("Failed to remove word from topic");
  }
}

export async function patchTopicWord(topicId, wordId, data) {
  const res = await fetch(`${API_BASE}/api/topics/${topicId}/words/${wordId}/`, {
    method: "PATCH",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update word type");
  return res.json();
}

// POST /api/topics/<topicId>/words/<wordId>/set-root/
export async function setWordRoot(topicId, wordId, rootText) {
  const res = await fetch(`${API_BASE}/api/topics/${topicId}/words/${wordId}/set-root/`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ root_text: rootText }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Failed to set root verb");
  return data;
}

// ---------- Quiz Session APIs ----------

// POST /api/learning/topics/<topicId>/quiz-sessions/ -> { id, topic_id, mode, started_at }
export async function startQuizSession(topicId, mode = "learn", itemOrder = []) {
  const res = await fetch(
    `${API_BASE}/api/learning/topics/${topicId}/quiz-sessions/`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ mode, item_order: itemOrder }),
    }
  );
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Failed to start quiz session");
  }
  return res.json();
}

// PATCH /api/learning/quiz-sessions/<sessionId>/ -> { id, items_seen, items_correct, current_index, score_percent }
export async function updateQuizSession(sessionId, { items_seen, items_correct, current_index, question_history }) {
  const res = await fetch(
    `${API_BASE}/api/learning/quiz-sessions/${sessionId}/`,
    {
      method: "PATCH",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ items_seen, items_correct, current_index, question_history }),
    }
  );
  if (!res.ok) throw new Error("Failed to update quiz session");
  return res.json();
}

// POST /api/learning/quiz-sessions/<sessionId>/complete/ -> session with score_percent
export async function completeQuizSession(sessionId) {
  const res = await fetch(
    `${API_BASE}/api/learning/quiz-sessions/${sessionId}/complete/`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({}),
    }
  );
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Failed to complete quiz session");
  }
  return res.json();
}

export async function fetchTopicProgression(topicId) {
  const res = await fetch(`${API_BASE}/api/learning/topics/${topicId}/progression/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to fetch topic progression");
  return res.json();
}

async function postTopicProgressionAction(topicId, actionPath) {
  const res = await fetch(`${API_BASE}/api/learning/topics/${topicId}/progression/${actionPath}/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Progression update failed");
  }
  return res.json();
}

export async function markVocabularyComplete(topicId) {
  return postTopicProgressionAction(topicId, "vocabulary-complete");
}

export async function markReadingComplete(topicId) {
  return postTopicProgressionAction(topicId, "reading-complete");
}

export async function incrementListeningPlay(topicId) {
  return postTopicProgressionAction(topicId, "listening-play");
}

export async function markRolePlayComplete(topicId) {
  return postTopicProgressionAction(topicId, "roleplay-complete");
}

export async function markQuizComplete(topicId) {
  return postTopicProgressionAction(topicId, "quiz-complete");
}

// GET /api/learning/topics/<topicId>/active-session/ -> { session: {...} | null }
export async function getActiveSession(topicId) {
  const res = await fetch(
    `${API_BASE}/api/learning/topics/${topicId}/active-session/`,
    { headers: authHeaders() }
  );
  if (!res.ok) throw new Error("Failed to fetch active session");
  return res.json();
}

// POST /api/learning/topics/<topicId>/reset-progress/ -> { result, progress }
export async function resetTopicProgress(topicId) {
  const res = await fetch(
    `${API_BASE}/api/learning/topics/${topicId}/reset-progress/`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({}),
    }
  );
  if (!res.ok) throw new Error("Failed to reset topic progress");
  return res.json();
}

// GET /api/learning/review/next-topic/ -> { topic: { id, name } | null, progress: {...} }
export async function fetchNextReviewTopic() {
  const res = await fetch(`${API_BASE}/api/learning/review/next-topic/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to fetch next review topic");
  return res.json();
}

// POST /api/learning/review/submit-result/ -> { result: "ok", overall_progress: {...} }
export async function submitReviewResult({ topic_id, session_id }) {
  const res = await fetch(`${API_BASE}/api/learning/review/submit-result/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ topic_id, session_id }),
  });
  if (!res.ok) throw new Error("Failed to submit review result");
  return res.json();
}

// GET /api/learning/progress/ -> { total, known, percent, topics: [...] }
export async function fetchOverallProgress() {
  const res = await fetch(`${API_BASE}/api/learning/progress/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to fetch overall progress");
  return res.json();
}

// ── TTS playback gate ─────────────────────────────────────────────────────────
// Mirrors the backend: /api/tts/ is HasAppAccess-gated, so users without app
// access (currently only management-only institute admins reach the shell in
// that state) must not call it. App.js sets this from /me; components use
// isAudioPlaybackAllowed() to hide their play buttons, and synthesizeSpeech
// short-circuits as a safety net so no blocked surface ever hits the network.
let _audioPlaybackAllowed = true;

export function setAudioPlaybackAllowed(allowed) {
  _audioPlaybackAllowed = !!allowed;
}

export function isAudioPlaybackAllowed() {
  return _audioPlaybackAllowed;
}

// POST /api/tts/ -> { audio_content: "<base64 MP3>", voice: "<voice name>" }
export async function synthesizeSpeech(text, speaker) {
  if (!_audioPlaybackAllowed) throw new Error("Audio playback requires a learner seat.");
  const res = await fetch(`${API_BASE}/api/tts/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ text, speaker }),
  });
  if (!res.ok) throw new Error("TTS request failed");
  return res.json();
}

// GET /api/stt-provider/ -> { provider: "google" | "openai" }
export async function getSttProvider() {
  const res = await fetch(`${API_BASE}/api/stt-provider/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to fetch STT provider");
  return res.json();
}

// PATCH /api/stt-provider/ (admin only) -> { provider: "<new provider>" }
export async function setSttProvider(provider) {
  const res = await fetch(`${API_BASE}/api/stt-provider/`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ provider }),
  });
  if (!res.ok) throw new Error("Failed to set STT provider");
  return res.json();
}

// POST /api/conversation/transcribe/ (multipart) -> { text: "<transcribed>", provider: "<active>" }
export async function transcribeSpeech(audioBlob, durationSeconds) {
  // Name the upload by its real container so the server picks the right
  // extension. iOS Safari's MediaRecorder emits audio/mp4, not webm/opus.
  const t = audioBlob.type || "";
  const ext = /mp4|aac|m4a|mpeg/.test(t) ? "mp4"
    : t.includes("ogg") ? "ogg"
    : t.includes("wav") ? "wav"
    : "webm";
  const formData = new FormData();
  formData.append("audio", audioBlob, `recording.${ext}`);
  // Optional: lets the server record accurate STT cost (per-second).
  if (durationSeconds) formData.append("duration_seconds", String(durationSeconds));
  const res = await fetch(`${API_BASE}/api/conversation/transcribe/`, {
    method: "POST",
    headers: authHeaders(),
    body: formData,
  });
  if (!res.ok) throw new Error("Transcription request failed");
  return res.json();
}

// POST /api/conversation/reply/ -> { text, audio_content, voice }
// scenarioTurns: [{order, speaker, arabic, translation}], turnIndex: which turn user is responding to,
// knownWords: Arabic word strings the student already knows.
// Pass userMessage="" and turnIndex=0 for the initial call to kick off the session.
export async function getConversationReply(
  topicName,
  topicDescription,
  history,
  userMessage,
  scenarioTurns = [],
  turnIndex = 0,
  knownWords = [],
  elapsedSeconds = 0,
  tutorMode = false,
  grammarRule = ""
) {
  const res = await fetch(`${API_BASE}/api/conversation/reply/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({
      topic_name: topicName,
      topic_description: topicDescription,
      history,
      user_message: userMessage,
      scenario_turns: scenarioTurns,
      turn_index: turnIndex,
      known_words: knownWords,
      elapsed_seconds: elapsedSeconds,
      tutor_mode: tutorMode,
      grammar_rule: grammarRule,
    }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    let detail = data.detail || data.error || "Conversation reply request failed";
    if (res.status === 429) detail = withRetryTime(detail, data.retry_at);
    const err = new Error(detail);
    err.code = data.error;
    err.status = res.status;
    err.retryAt = data.retry_at || null;
    throw err;
  }
  return res.json();
}

// POST /api/conversation/convert-to-topic/ -> { topic_id, topic_name, vocab_count, scenario_turns, ... }
// Admin only. Converts a free chat history into a structured learning topic.
// grammarRule: optional grammar rule code (e.g. "nominal_sentence") to persist on the topic.
export async function convertFreeChatToTopic(history, topicName, grammarRule = "") {
  const res = await fetch(`${API_BASE}/api/conversation/convert-to-topic/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ history, topic_name: topicName, grammar_rule: grammarRule }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || "Conversion failed");
  }
  return res.json();
}

// POST /api/admin/video-transcription/transcribe/ (admin only)
export async function transcribeYouTubeVideo({ youtubeUrl, provider, durationSeconds, startSeconds = 0 }) {
  const res = await fetch(`${API_BASE}/api/admin/video-transcription/transcribe/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({
      youtube_url: youtubeUrl,
      provider,
      duration_seconds: durationSeconds,
      start_seconds: startSeconds,
    }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || "Video transcription failed");
  }
  return res.json();
}

// POST /api/admin/video-transcription/generate-topic/ (admin only)
export async function generateTopicDraftFromTranscript({ title, transcript, description = "", order = null, scenario = null }) {
  const res = await fetch(`${API_BASE}/api/admin/video-transcription/generate-topic/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ title, transcript, description, order, scenario }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || "Failed to generate topic draft");
  }
  return res.json();
}

// POST /api/admin/video-transcription/save-topic/ (admin only)
export async function saveVideoTranscriptionTopic({
  importContent,
  youtubeUrl,
  provider,
  durationSeconds,
  startSeconds = 0,
  transcriptText = "",
  includeDuplicates = false,
}) {
  const res = await fetch(`${API_BASE}/api/admin/video-transcription/save-topic/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({
      import_content: importContent,
      youtube_url: youtubeUrl,
      provider,
      duration_seconds: durationSeconds,
      start_seconds: startSeconds,
      transcript_text: transcriptText,
      include_duplicates: includeDuplicates,
    }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || "Failed to save topic");
  }
  return res.json();
}

// POST /api/admin/video-transcription/refine-transcript/ (admin only)
// Adds harakat (tashkeel) and fixes spelling using an OpenAI chat model.
export async function refineVideoTranscript({ transcript, model = "" }) {
  const res = await fetch(`${API_BASE}/api/admin/video-transcription/refine-transcript/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ transcript, model }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || "Failed to refine transcript");
  }
  return res.json();
}

// GET /api/learning/topics/<topicId>/grammar-questions/
// Returns static grammar drill questions for the topic's grammar rule (or empty list).
export async function fetchGrammarQuestions(topicId) {
  const res = await fetch(`${API_BASE}/api/learning/topics/${topicId}/grammar-questions/`, {
    headers: authHeaders(),
  });
  if (!res.ok) return { questions: [], rule: null };
  return res.json();
}

// ── Hardcoded interactive grammar lessons ────────────────────────────────────

// GET /api/learning/topics/<topicId>/grammar-lesson/ -> { topic_id, lesson, progress }
export async function fetchGrammarLesson(topicId) {
  const res = await fetch(`${API_BASE}/api/learning/topics/${topicId}/grammar-lesson/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to load the grammar lesson");
  return res.json();
}

// POST .../grammar-lesson/section-complete/ -> { topic_id, progress }
// 409 when attempted out of order (surfaced as an Error).
export async function completeGrammarLessonSection(topicId, sectionKey) {
  const res = await fetch(`${API_BASE}/api/learning/topics/${topicId}/grammar-lesson/section-complete/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ section: sectionKey }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not save section progress");
  }
  return res.json();
}

// POST .../grammar-lesson/quiz-complete/ -> { topic_id, progress }
export async function completeGrammarLessonQuiz(topicId, scorePercent) {
  const res = await fetch(`${API_BASE}/api/learning/topics/${topicId}/grammar-lesson/quiz-complete/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ score_percent: scorePercent }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Could not save the quiz score");
  }
  return res.json();
}

// GET /api/reading/config/ -> { enabled, is_staff, tier, translation_max_chars,
//   ocr_high_res, ocr_remaining_today, translations_remaining_today, ... }
// Ungated: the public /reader page calls this first to decide what to render
// (it can't call getDailyUsage, which needs auth).
export async function fetchReadingConfig() {
  const res = await fetch(`${API_BASE}/api/reading/config/`, { headers: authHeaders() });
  if (!res.ok) throw await apiError(res, "Could not load the reader config");
  return res.json();
}

// ---------- Typing tutor ----------

// GET /api/typing/config/ -> { enabled, is_staff }
// Ungated: the public /typing page calls this first to decide what to render.
export async function fetchTypingConfig() {
  const res = await fetch(`${API_BASE}/api/typing/config/`, { headers: authHeaders() });
  if (!res.ok) throw await apiError(res, "Could not load the typing tutor config");
  return res.json();
}

// GET /api/typing/layout/ -> { keys, row_order, harakat }
// The Arabic 101 key map. Fetched once so the frontend keeps no second copy.
export async function fetchTypingLayout() {
  const res = await fetch(`${API_BASE}/api/typing/layout/`, { headers: authHeaders() });
  if (!res.ok) throw await apiError(res, "Could not load the keyboard layout");
  return res.json();
}

// GET /api/typing/lessons/ -> { lessons, weak_keys }
export async function fetchTypingLessons() {
  const res = await fetch(`${API_BASE}/api/typing/lessons/`, { headers: authHeaders() });
  if (!res.ok) throw await apiError(res, "Could not load the typing lessons");
  return res.json();
}

// GET /api/typing/lessons/<code>/ -> { lesson, progress }
export async function fetchTypingLesson(code) {
  const res = await fetch(`${API_BASE}/api/typing/lessons/${encodeURIComponent(code)}/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw await apiError(res, "Could not load the lesson");
  return res.json();
}

// POST /api/typing/lessons/<code>/drill-complete/ -> { progress, weak_keys }
// 409 when attempted out of order (surfaced as an Error).
export async function completeTypingDrill(code, { drill, wpm, accuracy, keyAttempts, keyErrors }) {
  const res = await fetch(
    `${API_BASE}/api/typing/lessons/${encodeURIComponent(code)}/drill-complete/`,
    {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({
        drill,
        wpm,
        accuracy,
        key_attempts: keyAttempts || {},
        key_errors: keyErrors || {},
      }),
    }
  );
  if (!res.ok) throw await apiError(res, "Could not save your result");
  return res.json();
}

// GET /api/typing/vocab-drill/ -> { words, count, skipped_untypeable }
// Fully-vowelled words from the caller's Word Bank.
export async function fetchTypingVocabDrill() {
  const res = await fetch(`${API_BASE}/api/typing/vocab-drill/`, { headers: authHeaders() });
  if (!res.ok) throw await apiError(res, "Could not load your vocabulary");
  return res.json();
}

// GET /api/learning/topics/<topicId>/verb-form-questions/
// Returns verb-form drill questions for eligible verbs in the topic (or empty list).
export async function fetchVerbFormQuestions(topicId) {
  const res = await fetch(`${API_BASE}/api/learning/topics/${topicId}/verb-form-questions/`, {
    headers: authHeaders(),
  });
  if (!res.ok) return { questions: [] };
  return res.json();
}

// ── Role-play session persistence ─────────────────────────────────────────

export async function getRolePlaySession(topicId) {
  const res = await fetch(`${API_BASE}/api/learning/topics/${topicId}/roleplay-session/`, {
    headers: authHeaders(),
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error("Failed to load role-play session");
  return res.json();
}

export async function saveRolePlaySession(topicId, { history, studentTurnIdx, sessionComplete }) {
  const res = await fetch(`${API_BASE}/api/learning/topics/${topicId}/roleplay-session/`, {
    method: "PUT",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({
      history,
      student_turn_idx: studentTurnIdx,
      session_complete: sessionComplete,
    }),
  });
  if (!res.ok) throw new Error("Failed to save role-play session");
  return res.json();
}

export async function deleteRolePlaySession(topicId) {
  const res = await fetch(`${API_BASE}/api/learning/topics/${topicId}/roleplay-session/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok && res.status !== 204) throw new Error("Failed to delete role-play session");
}

// ── AI model selection ────────────────────────────────────────────────────

export async function getAiModel() {
  const res = await fetch(`${API_BASE}/api/ai-model/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to load AI model");
  return res.json(); // { model: "..." }
}

export async function setAiModel(model) {
  const res = await fetch(`${API_BASE}/api/ai-model/`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ model }),
  });
  if (!res.ok) throw new Error("Failed to save AI model");
  return res.json();
}

// ── Usage limits ──────────────────────────────────────────────────────────

export async function getUsageLimits() {
  const res = await fetch(`${API_BASE}/api/usage-limits/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to load usage limits");
  return res.json(); // { max_daily_roleplay, free_chat_minutes, max_response_seconds }
}

// Admin: aggregated AI/STT/TTS/OCR cost rollup over the last N days.
export async function fetchUsageCosts(days = 30) {
  const res = await fetch(`${API_BASE}/api/admin/usage-costs/?days=${days}`, { headers: authHeaders() });
  if (!res.ok) throw new Error("Failed to load usage costs");
  return res.json();
}

// Admin: compare recorded estimate vs provider-billed actual (OpenAI / Google).
export async function fetchUsageCostsReconcile(days = 30) {
  const res = await fetch(`${API_BASE}/api/admin/usage-costs/reconcile/?days=${days}`, { headers: authHeaders() });
  if (!res.ok) throw new Error("Failed to reconcile costs");
  return res.json();
}

// The caller's monthly AI-usage meter ({ active, enforced, percent_used,
// resets_at } — just { active: false } outside soft/hard metering). Dollar
// figures and metering internals are intentionally not exposed.
export async function fetchAiCredit() {
  const res = await fetch(`${API_BASE}/api/ai-credit/`, { headers: authHeaders() });
  if (!res.ok) throw new Error("Failed to load AI credit");
  return res.json();
}

export async function setUsageLimits(data) {
  const res = await fetch(`${API_BASE}/api/usage-limits/`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to save usage limits");
  return res.json();
}

export async function getDailyUsage() {
  const res = await fetch(`${API_BASE}/api/learning/daily-usage/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to load daily usage");
  return res.json(); // { roleplay_count, free_chat_seconds, limits: {...} }
}

// PATCH /api/auth/profile/ -> { id, email, is_staff, display_name }
export async function updateProfile(fields = {}) {
  // Accepts { first_name, last_name, country, display_name }
  const res = await fetch(`${API_BASE}/api/auth/profile/`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(fields),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Failed to update profile");
  }
  return res.json();
}

// GET /api/learning/leaderboard/?period=alltime|monthly -> leaderboard entries
export async function fetchLeaderboard(period = "alltime") {
  const res = await fetch(`${API_BASE}/api/learning/leaderboard/?period=${period}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to load leaderboard");
  return res.json();
}

// POST /api/auth/change-password/ -> { token }
export async function changePassword(currentPassword, newPassword) {
  const res = await fetch(`${API_BASE}/api/auth/change-password/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Failed to change password");
  }
  return res.json();
}

// ---------- Global pronoun annotations ----------

export async function fetchGlobalPronounAnnotations() {
  const res = await fetch(`${API_BASE}/api/global-pronoun-annotations/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to fetch global pronoun annotations");
  return res.json(); // { normalized_text: annotation, ... }
}

export async function setGlobalPronounAnnotation(normalizedText, annotation) {
  const res = await fetch(`${API_BASE}/api/global-pronoun-annotations/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ normalized_text: normalizedText, annotation }),
  });
  if (!res.ok) throw new Error("Failed to save global pronoun annotation");
  return res.json();
}

export async function deleteGlobalPronounAnnotation(normalizedText) {
  const res = await fetch(`${API_BASE}/api/global-pronoun-annotations/`, {
    method: "DELETE",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ normalized_text: normalizedText }),
  });
  if (!res.ok) throw new Error("Failed to delete global pronoun annotation");
}

// POST /api/words/<wordId>/fetch-definition/ -> { word, imported, message }
export async function fetchDefinitionFromDictionary(wordId, force = false, preview = false, searchWord = null) {
  const body = { force, preview };
  if (searchWord) body.search_word = searchWord;
  const res = await fetch(`${API_BASE}/api/words/${wordId}/fetch-definition/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to fetch definition from dictionary");
  }
  return res.json();
}

// POST /api/words/<wordId>/fetch-definition-ai/ -> { word, imported, message }
// wordTypeHint ("vocab" | "verb" | "expression") makes the admin's reading
// authoritative instead of letting the model guess between homographs;
// contextSentence is the line the word was clicked in.
export async function fetchDefinitionFromAI(
  wordId,
  { force = false, preview = false, searchWord = null, model = "", topicId = null,
    wordTypeHint = "", contextSentence = "" } = {},
) {
  const body = { force, preview };
  if (searchWord) body.search_word = searchWord;
  if (model) body.model = model;
  if (topicId) body.topic_id = topicId;
  if (wordTypeHint) body.word_type_hint = wordTypeHint;
  if (contextSentence) body.context_sentence = contextSentence;
  const res = await fetch(`${API_BASE}/api/words/${wordId}/fetch-definition-ai/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await apiError(res, "Failed to fetch definition from AI");
  return res.json();
}

// POST /api/words/<id>/generate-morphology/  { force?, model? }
// -> { morphology, cached }  (admin only; generates & stores the §9 word-structure)
export async function generateWordMorphology(wordId, { force = false, model = "" } = {}) {
  const body = { force };
  if (model) body.model = model;
  const res = await fetch(`${API_BASE}/api/words/${wordId}/generate-morphology/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await apiError(res, "Failed to generate word structure");
  return res.json();
}

// POST /api/admin/topics/<id>/generate-morphology/  { force?, model? }
// -> { generated, skipped, failed, total_scenario_words, failures }  (admin only)
export async function generateTopicMorphology(topicId, { force = false, model = "" } = {}) {
  const body = { force };
  if (model) body.model = model;
  const res = await fetch(`${API_BASE}/api/admin/topics/${topicId}/generate-morphology/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await apiError(res, "Failed to generate word structures");
  return res.json();
}

// POST /api/admin/scenario-turns/<id>/generate-morphology/
//   { force?, model?, token_index? }
// -> { generated, skipped, failed, total_words, failures, word_morphology }
// Morphology is stored per token on the turn, in the sentence's context (#92).
// Pass token_index to (re)generate the structure for a single word — used by
// the admin "regenerate structure" action when a word's structure is wrong.
export async function generateTurnMorphology(
  turnId,
  { force = false, model = "", tokenIndex = undefined } = {},
) {
  const body = { force };
  if (model) body.model = model;
  if (tokenIndex !== undefined && tokenIndex !== null) body.token_index = tokenIndex;
  const res = await fetch(`${API_BASE}/api/admin/scenario-turns/${turnId}/generate-morphology/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await apiError(res, "Failed to generate word structures for this line");
  return res.json();
}

// ── Public Iʿrāb tool ──────────────────────────────────────────────────────

// GET /api/morphology/config/
// -> { enabled, max_chars, sentences_remaining_today, engine_available }
export async function fetchMorphologyConfig() {
  const res = await fetch(`${API_BASE}/api/morphology/config/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw await apiError(res, "Failed to load Iʿrāb tool settings");
  return res.json();
}

// POST /api/morphology/analyze/  { text }
// -> { sentence, tokens: { "<idx>": { morphology, source, refinable } },
//      refine_needed: ["<idx>"...], engine_available }
// The instant, AI-free pass — cache + local engine only.
export async function analyzeMorphology(text) {
  const res = await fetch(`${API_BASE}/api/morphology/analyze/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw await apiError(res, "Failed to analyze the sentence");
  return res.json();
}

// POST /api/morphology/peek/  { text }
// -> { sentence, tokens } — cache-read-only snapshot, polled during a
// running refine so words upgrade progressively. No AI, no quota.
export async function peekMorphology(text) {
  const res = await fetch(`${API_BASE}/api/morphology/peek/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw await apiError(res, "peek failed");
  return res.json();
}

// POST /api/morphology/refine/  { text, token_indices? }
// -> { tokens: { "<idx>": { morphology, source, refinable } }, failures }
// The metered AI pass. A 402 (credit exhausted) still carries the tokens
// refined before credit ran out — exposed on the thrown error as .tokens.
export async function refineMorphology(text, tokenIndices = undefined) {
  const body = { text };
  if (tokenIndices !== undefined) body.token_indices = tokenIndices;
  const res = await fetch(`${API_BASE}/api/morphology/refine/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const clone = res.clone(); // apiError consumes the body; clone first
    const err = await apiError(res, "Failed to refine the analysis");
    if (res.status === 402) {
      const data = await clone.json().catch(() => ({}));
      err.tokens = data.tokens || {};
      err.resetsAt = data.resets_at || null;
    }
    return Promise.reject(err);
  }
  return res.json();
}

// POST /api/morphology/verify/  { text, token_index, verified? }
// -> { verified }  (staff only; flags the word's cached analysis as
// reviewed-correct — the Track-2 gold set)
export async function verifyMorphologyToken(text, tokenIndex, verified = true) {
  const res = await fetch(`${API_BASE}/api/morphology/verify/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ text, token_index: tokenIndex, verified }),
  });
  if (!res.ok) throw await apiError(res, "Failed to update the verified flag");
  return res.json();
}

// GET /api/admin/morphology-quality/?days=N
// -> { days, overall, by_source, top_disagreements, verified_count }
// Local-engine-vs-AI agreement telemetry (staff only).
export async function fetchMorphologyQuality(days = 30) {
  const res = await fetch(`${API_BASE}/api/admin/morphology-quality/?days=${days}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw await apiError(res, "Failed to load Iʿrāb quality stats");
  return res.json();
}

// ── Public Verb Conjugator tool ────────────────────────────────────────────

// GET /api/conjugator/config/
// -> { enabled, tier, is_staff, max_chars, lookups_remaining_today,
//      subscriber_daily_lookups }
export async function fetchConjugatorConfig() {
  const res = await fetch(`${API_BASE}/api/conjugator/config/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw await apiError(res, "Failed to load conjugator settings");
  return res.json();
}

// POST /api/conjugator/conjugate/  { text }
// -> { query, source, is_verb, result: { lemma, root, form, pattern,
//      definition, masdar, ism_faail, ism_mafool, has_passive, matched,
//      conjugations }, alternatives, related, tier, remaining_today }
// A 429 (daily quota) carries retry_at and, for the free tier, signup_cta —
// apiError exposes them as .status / .retryAt / .signupCta for the upsell card.
export async function conjugateVerb(text) {
  const res = await fetch(`${API_BASE}/api/conjugator/conjugate/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw await apiError(res, "Failed to conjugate the verb");
  return res.json();
}

// POST /api/admin/words/analyze/  { text, model? }
// -> { analysis, pos, conjugations_count, model }  (admin only; does NOT save)
export async function analyzeNewWordAI(text, model = "") {
  const body = { text };
  if (model) body.model = model;
  const res = await fetch(`${API_BASE}/api/admin/words/analyze/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await apiError(res, "Failed to analyze word with AI");
  return res.json();
}

// POST /api/admin/words/  { text, pos, transliteration?, definition?, analysis? }
// -> { word, imported, message }  (admin only; creates a public word)
// On a duplicate the server returns 409; the thrown error carries .status=409
// and .word so the caller can open the existing word instead.
export async function createAdminWord(payload) {
  const res = await fetch(`${API_BASE}/api/admin/words/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = new Error(data.detail || data.error || "Failed to create word");
    e.status = res.status;
    e.word = data.word || null;
    throw e;
  }
  return data;
}

export async function fetchSenseParadigm(senseId) {
  const res = await fetch(`${API_BASE}/api/senses/${senseId}/paradigm/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to load paradigm");
  return res.json();
}

export async function createConjugation(senseId, { text, tags }) {
  const res = await fetch(`${API_BASE}/api/senses/${senseId}/conjugations/`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ text, tags }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Failed to create conjugation");
  return data;
}

export async function deleteConjugation(id) {
  const res = await fetch(`${API_BASE}/api/conjugations/${id}/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to delete conjugation");
}

// DELETE/POST /api/words/<id>/conjugation-link/ -> updated word (admin only).
// Vetoes (or restores) the automatic "this word is a form of <verb>" match,
// which can misfire because words are matched on ḥarakāt-stripped text.
export async function setWordConjugationLink(wordId, linked) {
  const res = await fetch(`${API_BASE}/api/words/${wordId}/conjugation-link/`, {
    method: linked ? "POST" : "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) throw await apiError(res, linked ? "Failed to restore the conjugation link" : "Failed to unlink the conjugation");
  return res.json();
}

export async function reorderConjugations(senseId, orderedIds) {
  const res = await fetch(`${API_BASE}/api/senses/${senseId}/conjugations/reorder/`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ ordered_ids: orderedIds }),
  });
  if (!res.ok) throw new Error("Failed to reorder conjugations");
}

// ─── Practice Call API ───────────────────────────────────────────────────────

export async function practiceLobbyJoin({ timezone, availability_slots, strict_level_matching = false }) {
  const res = await fetch(`${API_BASE}/api/practice/lobby/join/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ timezone, availability_slots, strict_level_matching }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Failed to join lobby");
  return data;
}

export async function practiceLobbyLeave() {
  const res = await fetch(`${API_BASE}/api/practice/lobby/leave/`, {
    method: "POST",
    headers: authHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Failed to leave lobby");
  return data;
}

export async function practiceLobbyStatus() {
  const res = await fetch(`${API_BASE}/api/practice/lobby/status/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to fetch lobby status");
  return res.json();
}

// GET /api/onboarding/audio/manifest/ -> { enabled, keys } — which of the
// /start topic-demo's fixed voice-over clips the server can serve (AllowAny;
// empty when TTS isn't configured).
export async function fetchOnboardingAudioManifest() {
  const res = await fetch(`${API_BASE}/api/onboarding/audio/manifest/`);
  if (!res.ok) throw new Error("Failed to fetch onboarding audio manifest");
  return res.json();
}

// Direct URL for one fixed onboarding demo clip (audio/mpeg, browser-cacheable).
export function onboardingAudioUrl(key) {
  return `${API_BASE}/api/onboarding/audio/${key}/`;
}

// POST /api/onboarding/complete/ — idempotent first-run-welcome dismissal.
export async function completeOnboarding() {
  const res = await fetch(`${API_BASE}/api/onboarding/complete/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Failed to record onboarding");
  return data;
}

export async function practicePledgeAccept() {
  const res = await fetch(`${API_BASE}/api/practice/pledge/accept/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Failed to record pledge");
  return data;
}

export async function practiceLobbyUpdateAvailability(availability_slots) {
  const res = await fetch(`${API_BASE}/api/practice/lobby/availability/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ availability_slots }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Failed to update availability");
  return data;
}

// ── Browsable open lobbies ──────────────────────────────────────────────────

export async function practiceOpenLobbies() {
  const res = await fetch(`${API_BASE}/api/practice/lobbies/`, {
    headers: authHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Failed to fetch open lobbies");
  return data;
}

export async function practiceCreateOpenLobby({ start_utc, end_utc, strict_level_matching = false, timezone, visibility = "public", capacity = 2 }) {
  const res = await fetch(`${API_BASE}/api/practice/lobbies/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ start_utc, end_utc, strict_level_matching, timezone, visibility, capacity }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.detail || "Failed to create lobby");
    err.pledgeRequired = !!data.pledge_required;
    throw err;
  }
  return data;
}

export async function practiceJoinOpenLobby(lobbyId, { timezone } = {}) {
  const res = await fetch(`${API_BASE}/api/practice/lobbies/${lobbyId}/join/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ timezone }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.detail || "Failed to join lobby");
    err.pledgeRequired = !!data.pledge_required;
    err.status = res.status;
    throw err;
  }
  return data;
}

export async function practiceCancelOpenLobby(lobbyId) {
  const res = await fetch(`${API_BASE}/api/practice/lobbies/${lobbyId}/cancel/`, {
    method: "POST",
    headers: authHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Failed to cancel lobby");
  return data;
}

export async function practiceSessionAccept(sessionId) {
  const res = await fetch(`${API_BASE}/api/practice/sessions/${sessionId}/accept/`, {
    method: "POST",
    headers: authHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Failed to accept session");
  return data;
}

export async function practiceSessionDecline(sessionId) {
  const res = await fetch(`${API_BASE}/api/practice/sessions/${sessionId}/decline/`, {
    method: "POST",
    headers: authHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Failed to decline session");
  return data;
}

export async function practiceSessionCancel(sessionId) {
  const res = await fetch(`${API_BASE}/api/practice/sessions/${sessionId}/cancel/`, {
    method: "POST",
    headers: authHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Failed to cancel session");
  return data;
}

export async function practiceSessionJoin(sessionId) {
  const res = await fetch(`${API_BASE}/api/practice/sessions/${sessionId}/join/`, {
    headers: authHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Failed to get call token");
  return data;
}

export async function practiceSessionComplete(sessionId, { force = false } = {}) {
  // Without force, leaving before the slot ends keeps the session rejoinable;
  // force=true ends it for both participants immediately.
  const res = await fetch(`${API_BASE}/api/practice/sessions/${sessionId}/complete/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ force }),
  });
  // Best-effort — ignore errors (session may already be completed)
  return res.ok;
}

export async function practiceSessionReport(sessionId, { general_issue = "", flagged_misgendering = false } = {}) {
  const res = await fetch(`${API_BASE}/api/practice/sessions/${sessionId}/report/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ general_issue, flagged_misgendering }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok && res.status !== 409) throw new Error(data.detail || "Failed to submit report");
  return data;
}

// ── Practice-call chat transcript (WhatsApp-style, persisted per call) ────────

// Replace the requesting user's chat for a session. messages: [{ role, arabic, translation, ts }]
export async function savePracticeCallChat(sessionId, messages) {
  const res = await fetch(`${API_BASE}/api/practice/sessions/${sessionId}/chat/`, {
    method: "PUT",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ messages }),
  });
  if (!res.ok) throw new Error("Failed to save chat");
  return res.json();
}

export async function fetchPracticeCallChat(sessionId) {
  const res = await fetch(`${API_BASE}/api/practice/sessions/${sessionId}/chat/`, { headers: authHeaders() });
  if (!res.ok) throw new Error("Failed to load chat");
  return res.json();
}

// List the user's recent call chats: [{ session, partner_username, message_count, updated_at }]
export async function fetchPracticeCallChats() {
  const res = await fetch(`${API_BASE}/api/practice/chats/`, { headers: authHeaders() });
  if (!res.ok) throw new Error("Failed to load past chats");
  return res.json();
}

export async function practiceSessionList() {
  const res = await fetch(`${API_BASE}/api/practice/sessions/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to list sessions");
  return res.json();
}

export async function practiceAdminSearchUsers(q) {
  const res = await fetch(`${API_BASE}/api/practice/admin/users/?q=${encodeURIComponent(q)}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Search failed");
  return res.json();
}

export async function practiceAdminResetUser(userId) {
  const res = await fetch(`${API_BASE}/api/practice/admin/reset-user/`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: userId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Reset failed");
  return data;
}

export async function practiceAdminMisgenderingReports() {
  const res = await fetch(`${API_BASE}/api/practice/admin/misgendering-reports/`, {
    headers: authHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Failed to load reports");
  return data;
}

export async function practiceAdminSetGender(userId, gender) {
  const res = await fetch(`${API_BASE}/api/practice/admin/set-gender/`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: userId, gender }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Failed to update gender");
  return data;
}

export async function practiceAdminSetDob(userId, dateOfBirth) {
  const res = await fetch(`${API_BASE}/api/practice/admin/set-dob/`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: userId, date_of_birth: dateOfBirth }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Failed to update date of birth");
  return data;
}

// =====================================================================
// QURANIC TOPICS API — completely separate from existing topic system
// =====================================================================

// GET /api/quran/chapters/ → { chapters: [...] }
export async function fetchQuranChapters() {
  const res = await fetch(`${API_BASE}/api/quran/chapters/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to load Quran chapters");
  return res.json();
}

// GET /api/quranic-topics/ → [...]
export async function fetchQuranicTopics() {
  const res = await fetch(`${API_BASE}/api/quran/quranic-topics/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to load Quranic topics");
  return res.json();
}

// POST /api/quranic-topics/ → topic
export async function createQuranicTopic(payload) {
  const res = await fetch(`${API_BASE}/api/quran/quranic-topics/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || JSON.stringify(data) || "Failed to create Quranic topic");
  }
  return res.json();
}

// GET /api/quranic-topics/{id}/ → topic with verses
export async function fetchQuranicTopicDetail(id) {
  const res = await fetch(`${API_BASE}/api/quran/quranic-topics/${id}/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to load Quranic topic");
  return res.json();
}

// DELETE /api/quranic-topics/{id}/
export async function deleteQuranicTopic(id) {
  const res = await fetch(`${API_BASE}/api/quran/quranic-topics/${id}/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to delete Quranic topic");
}

// GET /api/quranic-topics/{id}/quiz/ → { questions: [...] }
export async function fetchQuranicQuiz(id) {
  const res = await fetch(`${API_BASE}/api/quran/quranic-topics/${id}/quiz/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to load Quranic quiz");
  return res.json();
}

// POST /api/quranic-topics/{id}/quiz/submit/ → { completed, score, total }
export async function submitQuranicQuiz(id, score, total) {
  const res = await fetch(`${API_BASE}/api/quran/quranic-topics/${id}/quiz/submit/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ score, total }),
  });
  if (!res.ok) throw new Error("Failed to submit quiz");
  return res.json();
}

// POST /api/quran/builtin-topics/{id}/quiz/submit/ → { completed, score, total }
export async function submitBuiltinQuranicQuiz(id, score, total, wordNorms = []) {
  const res = await fetch(`${API_BASE}/api/quran/builtin-topics/${id}/quiz/submit/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ score, total, word_norms: wordNorms }),
  });
  if (!res.ok) throw new Error("Failed to submit builtin quiz");
  return res.json();
}

// GET /api/quranic-topics/{id}/word-detail/?verse_key=X&position=Y
export async function fetchQuranicWordDetail(id, verse_key, position) {
  const params = new URLSearchParams({ verse_key, position });
  const res = await fetch(`${API_BASE}/api/quran/quranic-topics/${id}/word-detail/?${params}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to load word detail");
  return res.json();
}

// ── Built-in Quran curriculum ──────────────────────────────────────────────────

// GET /api/quran/builtin-topics/ → [...]
export async function fetchBuiltinQuranicTopics() {
  const res = await fetch(`${API_BASE}/api/quran/builtin-topics/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to load built-in Quranic topics");
  return res.json();
}

// GET /api/quran/builtin-topics/{id}/ → topic with verses
export async function fetchBuiltinQuranicTopicDetail(id) {
  const res = await fetch(`${API_BASE}/api/quran/builtin-topics/${id}/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to load built-in Quranic topic");
  return res.json();
}

// GET /api/quran/builtin-topics/{id}/word-detail/?verse_key=X&position=Y
export async function fetchBuiltinQuranicWordDetail(id, verse_key, position) {
  const params = new URLSearchParams({ verse_key, position });
  const res = await fetch(`${API_BASE}/api/quran/builtin-topics/${id}/word-detail/?${params}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to load word detail");
  return res.json();
}

// GET /api/quran/builtin-topics/{id}/quiz/ → { questions: [...] }
export async function fetchBuiltinQuranicQuiz(id, retake = false) {
  const url = retake
    ? `${API_BASE}/api/quran/builtin-topics/${id}/quiz/?retake=1`
    : `${API_BASE}/api/quran/builtin-topics/${id}/quiz/`;
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) throw new Error("Failed to load quiz");
  return res.json();
}

// GET /api/quran/word-metadata/?surah=X&ayah_start=Y&ayah_end=Z → [...]
export async function fetchQuranWordMetadata(surah, ayah_start, ayah_end) {
  const params = new URLSearchParams({ surah, ayah_start, ayah_end });
  const res = await fetch(`${API_BASE}/api/quran/word-metadata/?${params}`, {
    headers: authHeaders(),
  });
  if (!res.ok) return [];
  return res.json();
}

// POST /api/quran/import-curriculum/ (admin) — multipart with quran_topics, quran_topics_index, quran_word_metadata files
export async function importQuranCurriculum(files) {
  const formData = new FormData();
  if (files.quran_topics) formData.append("quran_topics", files.quran_topics);
  if (files.quran_topics_index) formData.append("quran_topics_index", files.quran_topics_index);
  if (files.quran_word_metadata) formData.append("quran_word_metadata", files.quran_word_metadata);
  const res = await fetch(`${API_BASE}/api/quran/import-curriculum/`, {
    method: "POST",
    headers: authHeaders(),
    body: formData,
  });
  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) {
    throw new Error(`Server error ${res.status}: the import endpoint returned an unexpected response. Make sure migrations have been applied (python manage.py migrate).`);
  }
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Import failed");
  return data;
}

// POST /api/quran/word-metadata/<pk>/link-verb/ (admin) — link metadata to a Sense
export async function linkQuranVerb(metadataId, senseId) {
  const res = await fetch(`${API_BASE}/api/quran/word-metadata/${metadataId}/link-verb/`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ sense_id: senseId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to link verb");
  }
  return res.json();
}

// DELETE /api/quran/word-metadata/<pk>/link-verb/ (admin) — unlink metadata from Sense
export async function unlinkQuranVerb(metadataId) {
  const res = await fetch(`${API_BASE}/api/quran/word-metadata/${metadataId}/link-verb/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to unlink verb");
  }
}

// PATCH /api/quran/word-metadata/<pk>/edit/ (admin) — correct morphology fields
// Returns { metadata, changed: [{field, original, corrected}] }
export async function patchQuranWordMetadata(metadataId, data) {
  const res = await fetch(`${API_BASE}/api/quran/word-metadata/${metadataId}/edit/`, {
    method: "PATCH",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.detail || "Failed to save corrections");
  return json;
}


// ── Builtin quiz word overrides (admin only) ────────────────────────────────

// GET /api/quran/builtin-topics/{id}/word-overrides/ → list of words + overrides
export async function fetchBuiltinTopicWordOverrides(topicId) {
  const res = await fetch(`${API_BASE}/api/quran/builtin-topics/${topicId}/word-overrides/`, {
    headers: { Authorization: `Token ${getToken()}` },
  });
  if (!res.ok) throw new Error("Failed to fetch word overrides");
  return res.json();
}

// POST /api/quran/builtin-topics/{id}/word-overrides/ → create/update override
export async function saveBuiltinTopicWordOverride(topicId, { verse_key, position, arabic, correct_meaning }) {
  const res = await fetch(`${API_BASE}/api/quran/builtin-topics/${topicId}/word-overrides/`, {
    method: "POST",
    headers: { Authorization: `Token ${getToken()}`, "Content-Type": "application/json" },
    body: JSON.stringify({ verse_key, position, arabic, correct_meaning }),
  });
  if (!res.ok) throw new Error("Failed to save override");
  return res.json();
}

// DELETE /api/quran/builtin-topics/{id}/word-overrides/
export async function deleteBuiltinTopicWordOverride(topicId, { verse_key, position }) {
  const res = await fetch(`${API_BASE}/api/quran/builtin-topics/${topicId}/word-overrides/`, {
    method: "DELETE",
    headers: { Authorization: `Token ${getToken()}`, "Content-Type": "application/json" },
    body: JSON.stringify({ verse_key, position }),
  });
  if (!res.ok) throw new Error("Failed to delete override");
}

// POST /api/topics/<id>/pdfs/ — upload a PDF attachment (admin only)
export async function uploadTopicPDF(topicId, file, title = "") {
  const formData = new FormData();
  formData.append("file", file);
  if (title) formData.append("title", title);
  const res = await fetch(`${API_BASE}/api/topics/${topicId}/pdfs/`, {
    method: "POST",
    headers: { Authorization: `Token ${getToken()}` }, // NO Content-Type — browser sets multipart boundary
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to upload PDF");
  }
  return res.json();
}

// DELETE /api/topic-pdfs/<id>/ — remove a PDF attachment (admin only)
export async function deleteTopicPDF(pdfId) {
  const res = await fetch(`${API_BASE}/api/topic-pdfs/${pdfId}/`, {
    method: "DELETE",
    headers: { Authorization: `Token ${getToken()}` },
  });
  if (!res.ok) throw new Error("Failed to delete PDF");
}

// ---------- Social feed (gender-segregated, fuṣḥā-only) ----------
//
// CONTRACT NOTE — createFeedPost resolves successfully on BOTH publish and
// fuṣḥā rejection: a rejection is a teaching payload ({post: {status:
// "rejected"}, feedback}), not an HTTP error. Branch on data.post.status.
// Real errors still throw, with extra routing fields: e.status, e.reason
// (403 gate / 409 duplicate), e.typingGate (403), e.retryAt (429).

async function feedError(res, fallback) {
  const data = await res.json().catch(() => ({}));
  const detail = data.detail || data.error || fallback;
  const e = new Error(res.status === 429 ? withRetryTime(detail, data.retry_at) : detail);
  e.status = res.status;
  e.reason = data.reason || null;
  e.typingGate = data.typing_gate || null;
  e.retryAt = data.retry_at || null;
  return e;
}

// GET /api/feed/eligibility/ — can this user read/post, and why not
export async function fetchFeedEligibility() {
  const res = await fetch(`${API_BASE}/api/feed/eligibility/`, { headers: authHeaders() });
  if (!res.ok) throw await feedError(res, "Failed to load feed status");
  return res.json();
}

// GET /api/feed/posts/ -> { results, next_cursor }
export async function fetchFeedPosts({ tab = "global", cursor = null, limit = 20, q = "" } = {}) {
  const params = new URLSearchParams({ tab, limit: String(limit) });
  if (cursor) params.set("cursor", cursor);
  if (q) params.set("q", q);
  const res = await fetch(`${API_BASE}/api/feed/posts/?${params}`, { headers: authHeaders() });
  if (!res.ok) throw await feedError(res, "Failed to load the feed");
  return res.json();
}

// POST /api/feed/posts/ { body } -> { post, feedback } (see CONTRACT NOTE)
export async function createFeedPost(body) {
  const res = await fetch(`${API_BASE}/api/feed/posts/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ body }),
  });
  if (!res.ok) throw await feedError(res, "Failed to publish");
  return res.json();
}

// GET /api/feed/posts/<id>/ -> { post }
export async function fetchFeedPost(postId) {
  const res = await fetch(`${API_BASE}/api/feed/posts/${postId}/`, { headers: authHeaders() });
  if (!res.ok) throw await feedError(res, "Post not found");
  return res.json();
}

// DELETE /api/feed/posts/<id>/ — soft delete (author or staff)
export async function deleteFeedPost(postId) {
  const res = await fetch(`${API_BASE}/api/feed/posts/${postId}/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok && res.status !== 204) throw await feedError(res, "Failed to delete");
}

// GET /api/feed/posts/<id>/feedback/ -> { reviews } (author/staff only)
export async function fetchFeedPostFeedback(postId) {
  const res = await fetch(`${API_BASE}/api/feed/posts/${postId}/feedback/`, { headers: authHeaders() });
  if (!res.ok) throw await feedError(res, "Failed to load feedback");
  return res.json();
}

// GET /api/feed/me/posts/?status= -> { results, next_cursor }
export async function fetchMyFeedPosts({ status = "", cursor = null, limit = 20 } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (status) params.set("status", status);
  if (cursor) params.set("cursor", cursor);
  const res = await fetch(`${API_BASE}/api/feed/me/posts/?${params}`, { headers: authHeaders() });
  if (!res.ok) throw await feedError(res, "Failed to load your posts");
  return res.json();
}

// POST /api/auth/handle/ { handle, display_name } — one-time feed identity
export async function setFeedHandle(handle, displayName) {
  const res = await fetch(`${API_BASE}/api/auth/handle/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ handle, display_name: displayName }),
  });
  if (!res.ok) throw await feedError(res, "Failed to save your handle");
  return res.json();
}

// GET /api/feed/posts/<id>/comments/ -> { results, next_cursor } (oldest first)
export async function fetchFeedComments(postId, cursor = null) {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  const res = await fetch(`${API_BASE}/api/feed/posts/${postId}/comments/?${params}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw await feedError(res, "Failed to load comments");
  return res.json();
}

// POST /api/feed/posts/<id>/comments/ { body } -> { comment, feedback }
// Same contract as createFeedPost: resolves on BOTH publish and rejection.
export async function createFeedComment(postId, body, parentId = null) {
  const res = await fetch(`${API_BASE}/api/feed/posts/${postId}/comments/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ body, parent_id: parentId }),
  });
  if (!res.ok) throw await feedError(res, "Failed to comment");
  return res.json();
}

export async function deleteFeedComment(commentId) {
  const res = await fetch(`${API_BASE}/api/feed/comments/${commentId}/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok && res.status !== 204) throw await feedError(res, "Failed to delete comment");
}

// POST/DELETE /api/feed/users/<id>/follow/ -> { following, follower_count }
export async function followFeedUser(userId) {
  const res = await fetch(`${API_BASE}/api/feed/users/${userId}/follow/`, {
    method: "POST",
    headers: authHeaders(),
  });
  if (!res.ok) throw await feedError(res, "Failed to follow");
  return res.json();
}

export async function unfollowFeedUser(userId) {
  const res = await fetch(`${API_BASE}/api/feed/users/${userId}/follow/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) throw await feedError(res, "Failed to unfollow");
  return res.json();
}

// GET /api/feed/users/<id>/ -> { user, results, next_cursor }
export async function fetchFeedUser(userId, cursor = null) {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  const res = await fetch(`${API_BASE}/api/feed/users/${userId}/?${params}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw await feedError(res, "User not found");
  return res.json();
}

// ---------- Feed moderation ----------

// POST /api/feed/reports/ { post_id | comment_id, reason, detail }
export async function reportFeedItem({ postId = null, commentId = null, reason, detail = "" }) {
  const res = await fetch(`${API_BASE}/api/feed/reports/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ post_id: postId, comment_id: commentId, reason, detail }),
  });
  if (!res.ok) throw await feedError(res, "Failed to send the report");
  return res.json();
}

// GET /api/feed/admin/reports/?status= -> { reports } (admin)
export async function fetchFeedReports(status = "open") {
  const res = await fetch(`${API_BASE}/api/feed/admin/reports/?status=${status}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw await feedError(res, "Failed to load reports");
  return res.json();
}

// POST /api/feed/admin/reports/<id>/resolve/ { action, note, suspend_days } (admin)
export async function resolveFeedReport(reportId, { action, note = "", suspendDays = null }) {
  const res = await fetch(`${API_BASE}/api/feed/admin/reports/${reportId}/resolve/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ action, note, suspend_days: suspendDays }),
  });
  if (!res.ok) throw await feedError(res, "Failed to resolve the report");
  return res.json();
}

// GET /api/feed/admin/rejections/?stage= -> { rejections } (admin)
export async function fetchFeedRejections(stage = "") {
  const params = stage ? `?stage=${stage}` : "";
  const res = await fetch(`${API_BASE}/api/feed/admin/rejections/${params}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw await feedError(res, "Failed to load rejections");
  return res.json();
}

// PATCH /api/feed/posts/<id>/ { body } -> { post, feedback } — edit-and-resubmit.
// Same contract as createFeedPost: resolves on both publish and rejection.
export async function updateFeedPost(postId, body) {
  const res = await fetch(`${API_BASE}/api/feed/posts/${postId}/`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ body }),
  });
  if (!res.ok) throw await feedError(res, "Failed to update the post");
  return res.json();
}

// POST /api/feed/posts/<id>/appeal/ -> { post, feedback } — full LLM check
// for a locally-rejected post (wordlist false-positive safety valve).
export async function appealFeedPost(postId) {
  const res = await fetch(`${API_BASE}/api/feed/posts/${postId}/appeal/`, {
    method: "POST",
    headers: authHeaders(),
  });
  if (!res.ok) throw await feedError(res, "Failed to appeal");
  return res.json();
}

// GET /api/feed/unread-count/ -> { unread }
export async function fetchFeedUnreadCount() {
  const res = await fetch(`${API_BASE}/api/feed/unread-count/`, { headers: authHeaders() });
  if (!res.ok) throw await feedError(res, "Failed to load unread count");
  return res.json();
}

// POST /api/feed/seen/ — stamp the visit (clears the unread badge)
export async function markFeedSeen() {
  const res = await fetch(`${API_BASE}/api/feed/seen/`, {
    method: "POST",
    headers: authHeaders(),
  });
  if (!res.ok) throw await feedError(res, "Failed to mark seen");
  return res.json();
}

// POST /api/feed/posts/<id>/vote/ { value: 1|-1|0 } -> { my_vote, like_count, down_count, score }
// Declarative: value is the desired end state (repeat = no-op, 0 clears).
export async function voteFeedPost(postId, value) {
  const res = await fetch(`${API_BASE}/api/feed/posts/${postId}/vote/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ value }),
  });
  if (!res.ok) throw await feedError(res, "Failed to vote");
  return res.json();
}

// POST /api/feed/comments/<id>/vote/ { value } — same semantics as post votes
export async function voteFeedComment(commentId, value) {
  const res = await fetch(`${API_BASE}/api/feed/comments/${commentId}/vote/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ value }),
  });
  if (!res.ok) throw await feedError(res, "Failed to vote");
  return res.json();
}

// POST /api/feed/check/ { body } — the FREE as-you-type fuṣḥā checker
// (deterministic local stage only; the LLM runs once, at publish).
// -> { verdict, can_publish, score_pct, flags: [{surface, marker, tier,
//      blocking, suggestions: [{text, note}], note_ar}], issues, word_count }
export async function checkFusha(body) {
  const res = await fetch(`${API_BASE}/api/feed/check/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ body }),
  });
  if (!res.ok) throw await feedError(res, "Failed to check the text");
  return res.json();
}

// GET /api/feed/comments/<id>/feedback/ -> { reviews } (comment author only)
export async function fetchFeedCommentFeedback(commentId) {
  const res = await fetch(`${API_BASE}/api/feed/comments/${commentId}/feedback/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw await feedError(res, "Failed to load feedback");
  return res.json();
}

// POST/DELETE /api/feed/users/<id>/block/ -> { blocked }
// Blocking hides the pair's content from each other and removes follows.
export async function blockFeedUser(userId) {
  const res = await fetch(`${API_BASE}/api/feed/users/${userId}/block/`, {
    method: "POST",
    headers: authHeaders(),
  });
  if (!res.ok) throw await feedError(res, "Failed to block");
  return res.json();
}

export async function unblockFeedUser(userId) {
  const res = await fetch(`${API_BASE}/api/feed/users/${userId}/block/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) throw await feedError(res, "Failed to unblock");
  return res.json();
}

// GET /api/feed/blocks/ -> { blocked: [author…] } — your own block list,
// the only route back to a blocked user's profile (to unblock).
export async function fetchFeedBlocks() {
  const res = await fetch(`${API_BASE}/api/feed/blocks/`, { headers: authHeaders() });
  if (!res.ok) throw await feedError(res, "Failed to load blocked users");
  return res.json();
}

// POST /api/app-connect/mint/ -> { code, expires_in } — a short-lived,
// single-use code the macOS app trades for this account's API token
// (see AppConnectPage; the app never sees the token itself).
export async function mintAppConnectCode(challenge) {
  const res = await fetch(`${API_BASE}/api/app-connect/mint/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(challenge ? { challenge } : {}),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Failed to create a connect code");
  }
  return res.json();
}

// ---------- Support widget ----------

// POST /api/support/requests/ — raise an issue / feedback / feature request.
export async function createSupportRequest({ category, subject, message }) {
  const res = await fetch(`${API_BASE}/api/support/requests/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ category, subject, message }),
  });
  if (!res.ok) throw await apiError(res, "Failed to send your request");
  return res.json();
}

// GET /api/support/requests/ -> the caller's own requests, newest first.
export async function listMySupportRequests() {
  const res = await fetch(`${API_BASE}/api/support/requests/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw await apiError(res, "Failed to load your requests");
  return res.json();
}

// GET /api/support/admin/requests/?status=open|resolved — staff only.
export async function adminListSupportRequests(status = "") {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  const res = await fetch(`${API_BASE}/api/support/admin/requests/${qs}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw await apiError(res, "Failed to load support requests");
  return res.json();
}

// POST /api/support/admin/requests/<id>/respond/ — record a response, email it
// to the user, and (by default) resolve. Returns { ..., email_sent }.
export async function adminRespondSupportRequest(id, { response, resolve = true }) {
  const res = await fetch(`${API_BASE}/api/support/admin/requests/${id}/respond/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ response, resolve }),
  });
  if (!res.ok) throw await apiError(res, "Failed to send the response");
  return res.json();
}

// POST /api/support/admin/requests/<id>/status/ — resolve without responding,
// or reopen. No email is sent.
export async function adminSetSupportRequestStatus(id, status) {
  const res = await fetch(`${API_BASE}/api/support/admin/requests/${id}/status/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw await apiError(res, "Failed to update the request");
  return res.json();
}

// ---------- AI topic quiz (docs/AI_TOPIC_QUIZ.md) ----------

// GET /api/learning/topics/<topicId>/ai-quiz/config/
// -> { enabled, is_staff, bank_ready, question_count, type_count, target_count,
//      line_count, questions_per_attempt, pass_percent, best_score, mastered,
//      in_progress_attempt_id, attempt_count }
export async function fetchAiQuizConfig(topicId) {
  const res = await fetch(`${API_BASE}/api/learning/topics/${topicId}/ai-quiz/config/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw await apiError(res, "Failed to load quiz settings");
  return res.json();
}

// POST /api/learning/topics/<topicId>/ai-quiz/attempts/  { resume }
// -> { id, questions: [...], reserve: [...], targets, results, current_index,
//      pass_percent }. Generates the topic's question bank on a first start
// (slow: 20–40 s). A 402 (AI credit exhausted) carries resets_at → err.resetsAt.
export async function startAiQuizAttempt(topicId, { resume = false } = {}) {
  const res = await fetch(`${API_BASE}/api/learning/topics/${topicId}/ai-quiz/attempts/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ resume }),
  });
  if (!res.ok) {
    const clone = res.clone();
    const err = await apiError(res, "Could not start the quiz");
    if (res.status === 402) {
      const data = await clone.json().catch(() => ({}));
      err.resetsAt = data.resets_at || null;
    }
    throw err;
  }
  return res.json();
}

// PATCH /api/learning/ai-quiz/attempts/<attemptId>/  { results: [...], current_index }
export async function patchAiQuizAttempt(attemptId, { results = [], currentIndex } = {}) {
  const res = await fetch(`${API_BASE}/api/learning/ai-quiz/attempts/${attemptId}/`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ results, current_index: currentIndex }),
  });
  if (!res.ok) throw await apiError(res, "Could not save your answer");
  return res.json();
}

// POST /api/learning/ai-quiz/attempts/<attemptId>/complete/  { results }
// -> { score_percent, pass_percent, passed, mastered, per_skill, missed_words,
//      best_score, progression }
export async function completeAiQuizAttempt(attemptId, { results = [] } = {}) {
  const res = await fetch(`${API_BASE}/api/learning/ai-quiz/attempts/${attemptId}/complete/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ results }),
  });
  if (!res.ok) throw await apiError(res, "Could not finish the quiz");
  return res.json();
}

// POST /api/learning/ai-quiz/questions/report/  { bank_id, question_id, reason, note }
export async function reportAiQuizQuestion({ bankId, questionId, reason, note = "" }) {
  const res = await fetch(`${API_BASE}/api/learning/ai-quiz/questions/report/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ bank_id: bankId, question_id: questionId, reason, note }),
  });
  if (!res.ok) throw await apiError(res, "Could not send the report");
  return res.json();
}

// POST /api/learning/topics/<topicId>/ai-quiz/regenerate/  (staff)
export async function regenerateAiQuiz(topicId) {
  const res = await fetch(`${API_BASE}/api/learning/topics/${topicId}/ai-quiz/regenerate/`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({}),
  });
  if (!res.ok) throw await apiError(res, "Could not regenerate the quiz");
  return res.json();
}

// ---------- AI topic quiz — staff bank editing ----------

// GET /api/learning/topics/<topicId>/ai-quiz/bank/  (staff)
// -> { topic_id, topic_name, prompt_version, targets, dialogue,
//      banks: [{ id, batch_no, model, created_at, questions: [{...q, bank_id, hidden, reports}] }] }
export async function fetchAiQuizBank(topicId) {
  const res = await fetch(`${API_BASE}/api/learning/topics/${topicId}/ai-quiz/bank/`, { headers: authHeaders() });
  if (!res.ok) throw await apiError(res, "Failed to load the question bank");
  return res.json();
}

// PATCH /api/learning/ai-quiz/banks/<bankId>/questions/<qid>/  (staff)
// patch: { prompt_en?, explanation?, difficulty?, audio_text?, data?, hidden? } -> the updated question
export async function updateAiQuizQuestion(bankId, questionId, patch) {
  const res = await fetch(`${API_BASE}/api/learning/ai-quiz/banks/${bankId}/questions/${encodeURIComponent(questionId)}/`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw await apiError(res, "Could not save the question");
  return res.json();
}

// DELETE /api/learning/ai-quiz/banks/<bankId>/questions/<qid>/  (staff)
export async function deleteAiQuizQuestion(bankId, questionId) {
  const res = await fetch(`${API_BASE}/api/learning/ai-quiz/banks/${bankId}/questions/${encodeURIComponent(questionId)}/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) throw await apiError(res, "Could not delete the question");
}
